import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import Editor from '@monaco-editor/react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';
import FileExplorer from './FileExplorer';
import PreviewPanel from './PreviewPanel';
import AIAssistant from './AIAssistant';
import SettingsModal from './SettingsModal';
import UserMenu from './UserMenu';
import AuthModal from './AuthModal';

const {
  FiSettings,
  FiEye,
  FiSidebar,
  FiExternalLink,
  FiLogIn,
} = FiIcons;
const { BiBot } = BiIcons;

const CodeEditor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentProject, files, activeFile, updateFile, saveCurrentProject } = useProject();
  const { settings } = useSettings();

  const [activePanel, setActivePanel] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);

  useEffect(() => {
    if (!currentProject) navigate('/');
  }, [currentProject, navigate]);

  // Auto-save (completely silent, no UI)
  useEffect(() => {
    if (settings.autoSave && currentProject) {
      if (autoSaveTimer) clearTimeout(autoSaveTimer);
      const timer = setTimeout(() => {
        saveCurrentProject();
      }, 2000);
      setAutoSaveTimer(timer);
      return () => clearTimeout(timer);
    }
  }, [files, settings.autoSave, currentProject, saveCurrentProject]);

  const handleEditorChange = (value) => {
    if (activeFile) updateFile(activeFile, value || '');
  };

  const getLanguage = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'js':
      case 'jsx':
        return 'javascript';
      case 'ts':
      case 'tsx':
        return 'typescript';
      case 'css':
        return 'css';
      case 'html':
        return 'html';
      case 'json':
        return 'json';
      case 'md':
        return 'markdown';
      default:
        return 'plaintext';
    }
  };

  const togglePanel = (panelType) => {
    setActivePanel(activePanel === panelType ? null : panelType);
  };

  const openPreviewInNewWindow = () => {
    if (!activeFile || !files[activeFile]) return;

    const fileExtension = activeFile.split('.').pop()?.toLowerCase();
    let content = '';

    if (fileExtension === 'html') {
      const htmlContent = files[activeFile];
      const cssContent = files['style.css'] || files['styles.css'] || '';
      const jsContent = files['script.js'] || files['app.js'] || '';

      content = htmlContent;
      if (cssContent) {
        const cssTag = `<style>${cssContent}</style>`;
        content = content.includes('</head>')
          ? content.replace('</head>', `${cssTag}</head>`)
          : `<head>${cssTag}</head>${content}`;
      }
      if (jsContent) {
        const jsTag = `<script>${jsContent}</script>`;
        content = content.includes('</body>')
          ? content.replace('</body>', `${jsTag}</body>`)
          : `${content}${jsTag}`;
      }
    } else {
      content = `<!DOCTYPE html><html><body><pre>${files[activeFile]
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</pre></body></html>`;
    }

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'width=1200,height=800');
    if (w) w.addEventListener('beforeunload', () => URL.revokeObjectURL(url));
  };

  if (!currentProject) return null;

  const sidebarWidth = sidebarCollapsed ? 0 : 320;
  const aiPanelWidth = activePanel === 'ai' ? 384 : 0;

  const getEditorContainerStyle = () => {
    if (activePanel === 'preview') {
      return { width: `calc(50% - ${sidebarWidth}px)`, minWidth: '300px' };
    }
    if (activePanel === 'ai') {
      return { width: `calc(100vw - ${sidebarWidth + aiPanelWidth}px)`, minWidth: '300px' };
    }
    return { width: `calc(100vw - ${sidebarWidth}px)`, minWidth: '300px' };
  };

  return (
    <div className="h-screen flex flex-col bg-black overflow-hidden">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between flex-shrink-0 z-10">
        <div className="flex items-center gap-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-400 hover:text-white transition-colors duration-200"
          >
            <img
              src="https://res.cloudinary.com/frontly/image/upload/v1756676497/Angry_Branding_Logo_ykea75.png"
              alt="Angry Coder"
              className="w-8 h-8 object-contain mr-3"
            />
            <span className="font-medium">Angry Coder</span>
          </button>
          <div className="w-px h-6 bg-gray-700" />
          <h1 className="text-white font-semibold text-lg">{currentProject.name}</h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`p-2 rounded-lg transition-all duration-200 ${
              sidebarCollapsed
                ? 'text-gray-400 hover:text-white hover:bg-gray-800'
                : 'text-blue-400 bg-blue-500/10 hover:bg-blue-500/20'
            }`}
          >
            <SafeIcon icon={FiSidebar} className="text-lg" />
          </button>

          <button
            onClick={() => togglePanel('preview')}
            className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ${
              activePanel === 'preview'
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <SafeIcon icon={FiEye} className="mr-2 text-lg" />
            <span className="font-medium">Preview</span>
          </button>

          <button
            onClick={openPreviewInNewWindow}
            disabled={!activeFile || !files[activeFile]}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiExternalLink} className="text-lg" />
          </button>

          <button
            onClick={() => togglePanel('ai')}
            className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ${
              activePanel === 'ai'
                ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <SafeIcon icon={BiBot} className="mr-2 text-lg" />
            <span className="font-medium">AI</span>
          </button>

          {user ? (
            <UserMenu onOpenSettings={() => setShowSettings(true)} />
          ) : (
            <button
              onClick={() => setShowAuth(true)}
              className="flex items-center px-3 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors duration-200"
            >
              <SafeIcon icon={FiLogIn} className="mr-2" />
              <span className="font-medium">Sign In</span>
            </button>
          )}

          <button
            onClick={() => setShowSettings(true)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiSettings} className="text-lg" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        <AnimatePresence>
          {!sidebarCollapsed && (
            <motion.div
              className="w-80 bg-gray-950 border-r border-gray-800 flex-shrink-0 z-20"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 320, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <FileExplorer />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          className="flex flex-col bg-black relative z-10"
          style={getEditorContainerStyle()}
          layout
        >
          {activeFile && (
            <div className="bg-gray-900 px-6 py-3 text-sm text-gray-300 border-b border-gray-800 flex items-center">
              <div className="w-3 h-3 rounded-full bg-orange-500 mr-3" />
              <span className="font-medium">{activeFile}</span>
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            <Editor
              height="100%"
              language={activeFile ? getLanguage(activeFile) : 'plaintext'}
              value={activeFile ? files[activeFile] || '' : ''}
              onChange={handleEditorChange}
              theme="vs-dark"
              options={{
                fontSize: settings.fontSize,
                minimap: { enabled: false },
                wordWrap: 'on',
                automaticLayout: true,
                scrollBeyondLastLine: false,
              }}
            />
          </div>
        </motion.div>

        <AnimatePresence>
          {activePanel === 'preview' && (
            <motion.div className="bg-black border-l border-gray-800 w-1/2">
              <PreviewPanel files={files} activeFile={activeFile} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {activePanel === 'ai' && (
            <motion.div className="w-96 bg-black border-l border-gray-800">
              <AIAssistant />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode="signin" />
    </div>
  );
};

export default CodeEditor;